import { BaseCommand } from "../commands/base-command";
import { OutputHandler } from "./handler";
import { OutputOptions } from "./types";
import { CrunchAccountServiceWithContext } from "@crunchdao/sdk";

export abstract class EnhancedBaseCommand extends BaseCommand {
  protected outputHandler: OutputHandler;

  constructor(options: any) {
    super(options);

    const outputOptions: OutputOptions = {
      format: options.output === "json" ? "json" : "human",
      quiet: options.quiet || false,
      verbose: options.verbose || false,
      exitOnError: true,
    };

    this.outputHandler = new OutputHandler(outputOptions);
  }

  protected output<T>(data: T, command?: string): void {
    this.outputHandler.output(data, command);
  }

  protected handleError(error: any): never {
    return this.outputHandler.error(error);
  }

  protected success(message: string, data?: any): void {
    this.outputHandler.success(message, data);
  }

  protected warn(message: string, data?: any): void {
    this.outputHandler.warn(message, data);
  }

  /**
   * Helper function to create enhanced error objects with consistent structure
   */
  protected createEnhancedError(
    message: string,
    errorName: string,
    errorCode: string,
    details: any,
    originalError?: any
  ): Error {
    const customError = new Error(message);
    customError.name = errorName;
    (customError as any).code = errorCode;
    (customError as any).details = {
      ...details,
      originalError: originalError?.message || String(originalError),
    };
    (customError as any).originalError = originalError?.message || String(originalError);
    (customError as any).originalStack = originalError?.stack;

    return customError;
  }

  /**
   * Generic transaction error handler for all commands
   * Handles common Solana transaction errors with user-friendly messages
   */
  protected handleTransactionError(error: any, crunchName?: string): never {
    // Check for coordinator not approved error
    const isCoordinatorNotApproved =
      error?.error?.errorCode?.code === "OnlyApprovedCoordinatorsAllowed";

    if (isCoordinatorNotApproved) {
      const coordinatorAddress = this.provider.wallet.publicKey.toBase58();

      const customError = this.createEnhancedError(
        `Coordinator ${coordinatorAddress} is not approved yet. Contact admin to approve your coordinator.`,
        "CoordinatorNotApprovedError",
        "COORDINATOR_NOT_APPROVED",
        {
          coordinatorAddress,
          errorCode: error.error?.errorCode?.code || "OnlyApprovedCoordinatorsAllowed",
          errorNumber: error.error?.errorCode?.number || 6002,
        },
        error
      );

      return this.handleError(customError);
    }

    // Check for coordinator seeds constraint violation (coordinator not registered)
    const isCoordinatorSeedsConstraint =
      error?.error?.errorCode?.code === "ConstraintSeeds" &&
      error?.error?.errorCode?.number === 2006 &&
      error.message?.includes("coordinator");

    if (isCoordinatorSeedsConstraint) {
      const coordinatorAddress = this.provider.wallet.publicKey.toBase58();

      const customError = this.createEnhancedError(
        `Coordinator ${coordinatorAddress} is not registered yet. You must register your coordinator before performing crunch operations.`,
        "CoordinatorNotRegisteredError",
        "COORDINATOR_NOT_REGISTERED",
        {
          coordinatorAddress,
          errorCode: error.error?.errorCode?.code || "ConstraintSeeds",
          errorNumber: error.error?.errorCode?.number || 2006,
          suggestion: "Register your coordinator first using: crunch-coordinator register",
        },
        error
      );

      return this.handleError(customError);
    }

    // Check for account not found error (Account does not exist)
    if (error.message && error.message.includes("Account does not exist or has no data")) {
      // Extract the missing account address from the error message
      const addressMatch = error.message.match(/[A-Za-z0-9]{32,44}/);
      const missingAddress = addressMatch ? addressMatch[0] : "unknown";

      // If crunchName is provided, check if the missing address is the crunch address
      if (crunchName && missingAddress !== "unknown") {
        try {
          // Import the service to get crunch address
          const crunchAccountService = CrunchAccountServiceWithContext({ program: this.program });
          const expectedCrunchAddress = crunchAccountService.getCrunchAddress(crunchName);

          // If the missing address matches the crunch address, it's a crunch not found error
          if (expectedCrunchAddress.toBase58() === missingAddress) {
            const customError = this.createEnhancedError(
              `Crunch "${crunchName}" not found at address ${missingAddress}. The crunch may not have been created yet.`,
              "CrunchNotFoundError",
              "CRUNCH_NOT_FOUND",
              {
                crunchName,
                crunchAddress: missingAddress,
                suggestion:
                  "Create the crunch first using: crunch create <name> <payoutAmount> <maxModelsPerCruncher>",
              },
              error
            );

            return this.handleError(customError);
          }
        } catch {
          // If we can't get the crunch address, fall through to generic handling
        }
      }

      // Generic account not found error (for models, derived accounts, etc.)
      const customError = this.createEnhancedError(
        `Required account not found at address ${missingAddress}. This could be a missing model, derived account, or other account needed for the operation.`,
        "AccountNotFoundError",
        "ACCOUNT_NOT_FOUND",
        {
          missingAddress,
          crunchName: crunchName || undefined,
          suggestion:
            "Verify all required accounts are properly initialized. Check if models need to be registered first.",
        },
        error
      );

      return this.handleError(customError);
    }

    // Check for SendTransactionError with insufficient funds (SOL)
    const isInsufficientFunds =
      error.name === "SendTransactionError" ||
      (error.transactionMessage &&
        error.transactionMessage.includes(
          "Attempt to debit an account but found no record of a prior credit"
        ));

    if (isInsufficientFunds) {
      const walletAddress = this.provider.wallet.publicKey.toBase58();
      const network = this.resolvedOptions.network || "localnet";

      let creditInstructions = "";
      if (network === "localnet" || network === "devnet") {
        creditInstructions = `Run: solana airdrop 2 ${walletAddress}`;
      } else if (network === "mainnet") {
        creditInstructions = "Transfer SOL to your wallet address";
      }

      const customError = this.createEnhancedError(
        `No credit on ${walletAddress}. ${creditInstructions}`,
        "InsufficientFundsError",
        "INSUFFICIENT_FUNDS",
        {
          walletAddress,
          network,
          creditInstructions,
        },
        error
      );

      return this.handleError(customError);
    }

    // Handle other transaction errors - you can add more specific handlers here
    return this.handleError(error);
  }
}
